// ============================================================================
//  JournalListPage — Liste des écritures comptables
// ============================================================================
import { ListPageSkeleton } from '@/components/ui/Skeleton'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui'
import { accountingApi } from '@/modules/accounting/api/accounting.api'
import { fmtXOF, toNum } from '@/lib/decimal'
import { Plus, FileText, Search } from 'lucide-react'

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  DRAFT:     { label: 'Brouillon', color: 'text-[#D97706] bg-[#FEF3C7]' },
  VALIDATED: { label: 'Validé',    color: 'text-[#059669] bg-[#ECFDF5]' },
  CANCELLED: { label: 'Annulé',    color: 'text-[#DC2626] bg-[#FEF2F2]' },
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })

export default function JournalListPage() {
  const nav = useNavigate()
  const [search, setSearch]   = useState('')
  const [status, setStatus]   = useState('')
  const [page, setPage]       = useState(1)

  const { data: fiscalYears = [] } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn:  accountingApi.getFiscalYears,
  })
  const currentFY = (fiscalYears as any[]).find((f: any) => f.status === 'OPEN' || f.status === 'SOFT_CLOSED')

  const { data, isLoading } = useQuery({
    queryKey: ['entries-list', status, search, page, currentFY?.id],
    queryFn:  () => accountingApi.getEntries({
      fiscalYearId: currentFY?.id,
      status:  status || undefined,
      search:  search || undefined,
      page,
      limit:   20,
    }),
    enabled: !!currentFY?.id,
  })

  const entries  = (data as any)?.entries ?? (data as any)?.items ?? (Array.isArray(data) ? data : [])
  const total    = (data as any)?.total ?? entries.length

  return (
    <PageContainer>
      <PageHeader
        title="Journal comptable"
        description="Toutes les écritures de l'exercice en cours"
        breadcrumb={[{ label: 'Comptabilité' }, { label: 'Journal' }]}
        actions={
          <button
            onClick={() => nav('/accounting/entries/new')}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle écriture
          </button>
        }
      />

      {/* Filtres */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input
            value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Rechercher une écriture..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]"
          />
        </div>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--tx2)] focus:outline-none focus:border-[var(--blue)]">
          <option value="">Tous les statuts</option>
          <option value="DRAFT">Brouillon</option>
          <option value="VALIDATED">Validé</option>
          <option value="CANCELLED">Annulé</option>
        </select>
        <span className="text-xs text-[var(--tx3)]">{total} écriture(s)</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[var(--bd)] rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--bd)] bg-[var(--surface-2)]">
              {['Date', 'Référence', 'Libellé', 'Journal', 'Débit', 'Crédit', 'Statut', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--tx3)] uppercase tracking-wide">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--bd)]">
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--tx3)] text-sm">Chargement…</td></tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center">
                  <FileText className="w-10 h-10 text-[var(--bd)] mx-auto mb-3" />
                  <p className="text-sm text-[var(--tx3)]">Aucune écriture trouvée</p>
                  <button onClick={() => nav('/accounting/entries/new')}
                    className="mt-3 text-xs text-[var(--blue)] hover:underline">
                    Créer la première écriture →
                  </button>
                </td>
              </tr>
            ) : (
              entries.map((e: any) => {
                const cfg = STATUS_CFG[e.status] ?? { label: e.status, color: 'text-[var(--tx3)] bg-[var(--surface-2)]' }
                return (
                  <tr key={e.id}
                    onClick={() => nav(`/accounting/entries/${e.id}`)}
                    className="hover:bg-[var(--surface-2)] cursor-pointer transition-colors">
                    <td className="px-4 py-3 text-sm text-[var(--tx3)]">{fmtDate(e.date)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-[var(--tx2)]">{e.reference ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--brand-dark)] max-w-xs truncate">{e.description}</td>
                    <td className="px-4 py-3 text-sm text-[var(--tx3)]">{e.journal?.code ?? '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--brand-dark)]">{toNum(e.totalDebit) > 0 ? fmtXOF(e.totalDebit) : '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--brand-dark)]">{toNum(e.totalCredit) > 0 ? fmtXOF(e.totalCredit) : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-xs text-[var(--blue)] hover:underline">Voir →</button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--bd)]">
            <span className="text-xs text-[var(--tx3)]">
              {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} sur {total}
            </span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 text-xs border border-[var(--bd)] rounded-lg disabled:opacity-40 hover:border-[var(--blue)] transition-colors">
                ← Précédent
              </button>
              <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-xs border border-[var(--bd)] rounded-lg disabled:opacity-40 hover:border-[var(--blue)] transition-colors">
                Suivant →
              </button>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
